import { useMemo, useRef, useCallback } from "react";
import {
  Box,
  Button,
  IconButton,
  TextField,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Paper,
  Chip,
} from "@mui/material";
import { Add, Delete, Remove } from "@mui/icons-material";
import { SpecialCharPanel } from "./FillInBlankEditor";

const DEFAULT_GRID = () => ({
  grid: [
    [{ f: "" }, { f: "Boys" }, { f: "Girls" }, { f: "Total" }],
    [{ f: "Like coffee" }, { b: 0 }, { b: 1 }, { b: 2 }],
    [{ f: "Did not like coffee" }, { b: 3 }, { b: 4 }, { b: 5 }],
    [{ f: "Total" }, { b: 6 }, { b: 7 }, { b: 8 }],
  ],
});

const COLUMN_SUM_GRID = () => ({
  style: "column-sum",
  grid: [
    [{ f: "" }, { f: "5" }, { b: 0 }, { f: "4" }],
    [{ f: "+" }, { b: 1 }, { f: "7" }, { b: 2 }],
    [{ f: "" }, { f: "9" }, { f: "3" }, { f: "4" }],
  ],
});

const VENN_2_GRID = () => ({
  shape: "venn",
  grid: [
    [{ f: "" }, { f: "In B (e.g. Factor of 12)" }, { f: "Not in B" }],
    [{ f: "In A (e.g. Multiple of 3)" }, { b: 0 }, { b: 1 }],
    [{ f: "Not in A" }, { b: 2 }, { b: 3 }],
  ],
});

const COLOUR_SHAPE_TILE_GRID = () => ({
  grid: [
    [{ f: "" }, { f: "square" }, { f: "circle" }, { f: "star" }, { f: "triangle" }],
    [{ f: "black (B)" }, { f: "B □" }, { b: 0 }, { b: 1 }, { b: 2 }],
    [{ f: "grey (G)" }, { b: 3 }, { f: "G ○" }, { f: "G ☆" }, { b: 4 }],
    [{ f: "white (W)" }, { b: 5 }, { b: 6 }, { f: "W ☆" }, { f: "W △" }],
  ],
});

// Primary + alternatives for the seven empty colour×shape cells (NL2013 Q33a).
const COLOUR_SHAPE_TILE_ANSWERS = () => [
  ["B ○", "B circle", "B o", "BO", "Black Circle", "black circle", "B○"],
  ["B ☆", "B star", "Black Star", "black star", "B☆", "B *"],
  ["B △", "B triangle", "Black Triangle", "black triangle", "B△"],
  ["G □", "G square", "Grey Square", "Gray Square", "grey square", "gray square", "G□"],
  ["G △", "G triangle", "Grey Triangle", "Gray Triangle", "grey triangle", "gray triangle", "G△"],
  ["W □", "W square", "White Square", "white square", "W□"],
  ["W ○", "W circle", "W o", "WO", "White Circle", "white circle", "W○"],
];

const isEditableCell = (cell) => cell && typeof cell.b === "number";
const isFixedCell = (cell) => cell && typeof cell.f === "string";

const plainCellText = (value) => {
  const raw = value ?? "";
  if (!raw) return "";
  if (!/<[a-z][\s\S]*>/i.test(raw)) return String(raw);
  if (typeof document === "undefined") {
    return String(raw).replace(/<[^>]+>/g, "").replace(/&nbsp;/gi, " ").trim();
  }
  const d = document.createElement("div");
  d.innerHTML = raw;
  return (d.textContent || d.innerText || "").replace(/\u00a0/g, " ").trim();
};

/** Compact plain field for short tile/table answers (e.g. "B ○"). */
const TableCellEditor = ({ value, onChange, placeholder, charTargetRef }) => {
  const inputRef = useRef(null);
  const plain = plainCellText(value);

  const registerActive = () => {
    if (!charTargetRef) return;
    charTargetRef.current = (char) => {
      const el = inputRef.current;
      const cur = plainCellText(value);
      if (!el) {
        onChange(cur + char);
        return;
      }
      const start = el.selectionStart ?? cur.length;
      const end = el.selectionEnd ?? start;
      const next = cur.slice(0, start) + char + cur.slice(end);
      onChange(next);
      requestAnimationFrame(() => {
        el.focus();
        const pos = start + char.length;
        el.setSelectionRange(pos, pos);
      });
    };
  };

  return (
    <TextField
      fullWidth
      size="small"
      value={plain}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      onFocus={registerActive}
      inputRef={(el) => {
        inputRef.current = el;
      }}
      inputProps={{
        style: { fontSize: "1.05rem", textAlign: "center", letterSpacing: "0.02em" },
      }}
      sx={{
        "& .MuiInputBase-root": { minHeight: 36 },
        "& .MuiInputBase-input": { py: 0.75, px: 0.75 },
      }}
    />
  );
};

const countEditableCells = (grid) => {
  if (!Array.isArray(grid)) return 0;
  let n = 0;
  grid.forEach((row) => {
    (row || []).forEach((cell) => {
      if (isEditableCell(cell)) n += 1;
    });
  });
  return n;
};

const reindexGrid = (grid) => {
  let idx = 0;
  return (grid || []).map((row) =>
    (row || []).map((cell) => {
      if (isEditableCell(cell)) {
        const next = { b: idx };
        idx += 1;
        return next;
      }
      if (isFixedCell(cell)) return { f: cell.f };
      return { f: "" };
    })
  );
};

const syncBlanksToGrid = (grid, blanks, blankMarks) => {
  const n = countEditableCells(grid);
  const nextBlanks = Array.from({ length: n }, (_, i) => {
    const existing = blanks?.[i];
    return Array.isArray(existing) && existing.length > 0 ? [...existing] : [""];
  });
  const nextMarks = Array.from({ length: n }, (_, i) => {
    const m = blankMarks?.[i];
    return m != null && m !== "" ? Math.max(1, parseInt(m, 10) || 1) : 1;
  });
  return { blanks: nextBlanks, blankMarks: nextMarks };
};

const TableFillEditor = ({
  blanks,
  setBlanks,
  blankMarks,
  setBlankMarks,
  blankLabels,
  setBlankLabels,
  tableLayout,
  setTableLayout,
  showMathTools = false,
}) => {
  const grid = tableLayout?.grid?.length ? tableLayout.grid : DEFAULT_GRID().grid;
  const charTargetRef = useRef(null);
  const insertIntoActive = useCallback((char) => charTargetRef.current?.(char), []);

  const editableCount = useMemo(() => countEditableCells(grid), [grid]);

  const applyGrid = (nextGrid, shape = tableLayout?.shape) => {
    const reindexed = reindexGrid(nextGrid);
    const synced = syncBlanksToGrid(reindexed, blanks, blankMarks);
    // shape === null clears a previous venn/column-sum stamp; undefined keeps current.
    const nextLayout = { grid: reindexed };
    if (shape) nextLayout.shape = shape;
    setTableLayout(nextLayout);
    setBlanks(synced.blanks);
    setBlankMarks(synced.blankMarks);
    if (setBlankLabels) {
      const n = countEditableCells(reindexed);
      setBlankLabels(Array.from({ length: n }, (_, i) => blankLabels?.[i] ?? ""));
    }
  };

  const applyColourShapeTiles = () => {
    const reindexed = reindexGrid(COLOUR_SHAPE_TILE_GRID().grid);
    const seeded = COLOUR_SHAPE_TILE_ANSWERS();
    setTableLayout({ grid: reindexed });
    setBlanks(seeded);
    setBlankMarks(Array.from({ length: seeded.length }, () => 1));
    if (setBlankLabels) {
      setBlankLabels(Array.from({ length: seeded.length }, () => ""));
    }
  };

  const setRows = (rows) => {
    const r = Math.max(2, Math.min(12, rows));
    const cols = grid[0]?.length || 4;
    const next = Array.from({ length: r }, (_, ri) => {
      if (ri < grid.length) return [...grid[ri]];
      return Array.from({ length: cols }, () => ({ f: "" }));
    });
    applyGrid(next);
  };

  const setCols = (cols) => {
    const c = Math.max(2, Math.min(8, cols));
    const next = grid.map((row) => {
      const cells = [...(row || [])];
      while (cells.length < c) cells.push({ f: "" });
      return cells.slice(0, c);
    });
    applyGrid(next);
  };

  const updateCell = (ri, ci, patch) => {
    const next = grid.map((row, r) =>
      row.map((cell, c) => (r === ri && c === ci ? { ...cell, ...patch } : cell))
    );
    applyGrid(next);
  };

  const toggleCellType = (ri, ci, mode) => {
    const next = grid.map((row, r) =>
      row.map((cell, c) => {
        if (r !== ri || c !== ci) return cell;
        if (mode === "editable") return { b: 0 };
        return { f: isFixedCell(cell) ? cell.f : "" };
      })
    );
    applyGrid(next);
  };

  const updateFixedText = (ri, ci, value) => {
    const next = grid.map((row, r) =>
      row.map((cell, c) => (r === ri && c === ci ? { f: value } : cell))
    );
    setTableLayout({ ...(tableLayout?.shape ? { shape: tableLayout.shape } : {}), grid: next });
  };

  const updateBlankAnswer = (blankIdx, ansIdx, value) => {
    const next = [...(blanks || [])];
    while (next.length <= blankIdx) next.push([""]);
    const row = [...(next[blankIdx] || [""])];
    row[ansIdx] = value;
    next[blankIdx] = row;
    setBlanks(next);
  };

  const addAcceptedAnswer = (blankIdx) => {
    const next = [...(blanks || [])];
    while (next.length <= blankIdx) next.push([""]);
    next[blankIdx] = [...(next[blankIdx] || [""]), ""];
    setBlanks(next);
  };

  const removeAcceptedAnswer = (blankIdx, ansIdx) => {
    const next = [...(blanks || [])];
    const row = [...(next[blankIdx] || [""])];
    if (row.length <= 1) return;
    row.splice(ansIdx, 1);
    next[blankIdx] = row;
    setBlanks(next);
  };

  const updateBlankMark = (blankIdx, value) => {
    const next = [...(blankMarks || [])];
    while (next.length <= blankIdx) next.push(1);
    next[blankIdx] = Math.max(1, parseInt(value, 10) || 1);
    setBlankMarks(next);
  };

  const updateBlankLabel = (blankIdx, value) => {
    if (!setBlankLabels) return;
    const next = [...(blankLabels || [])];
    while (next.length <= blankIdx) next.push("");
    next[blankIdx] = value;
    setBlankLabels(next);
  };

  const selectedEditable = [];
  grid.forEach((row, ri) => {
    row.forEach((cell, ci) => {
      if (isEditableCell(cell)) {
        selectedEditable.push({ ri, ci, b: cell.b });
      }
    });
  });

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
        Table layout — fixed headers/labels and editable answer cells (also used for Venn-diagram
        regions: one cell per region — intersection, each-circle-only and outside-both)
      </Typography>

      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2, alignItems: "center" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>Rows</Typography>
          <IconButton size="small" onClick={() => setRows(grid.length - 1)} disabled={grid.length <= 2}>
            <Remove fontSize="small" />
          </IconButton>
          <Typography variant="body2" sx={{ minWidth: 24, textAlign: "center" }}>{grid.length}</Typography>
          <IconButton size="small" onClick={() => setRows(grid.length + 1)} disabled={grid.length >= 12}>
            <Add fontSize="small" />
          </IconButton>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>Columns</Typography>
          <IconButton size="small" onClick={() => setCols((grid[0]?.length || 4) - 1)} disabled={(grid[0]?.length || 0) <= 2}>
            <Remove fontSize="small" />
          </IconButton>
          <Typography variant="body2" sx={{ minWidth: 24, textAlign: "center" }}>{grid[0]?.length || 0}</Typography>
          <IconButton size="small" onClick={() => setCols((grid[0]?.length || 4) + 1)} disabled={(grid[0]?.length || 0) >= 8}>
            <Add fontSize="small" />
          </IconButton>
        </Box>
        <Chip label={`${editableCount} editable cell${editableCount !== 1 ? "s" : ""}`} size="small" color="primary" variant="outlined" />
        <Button
          size="small"
          variant="outlined"
          onClick={() => applyGrid(COLUMN_SUM_GRID().grid, "column-sum")}
          sx={{ ml: "auto", textTransform: "none" }}
        >
          Column sum
        </Button>
        <Button
          size="small"
          variant="outlined"
          onClick={() => applyGrid(VENN_2_GRID().grid, "venn")}
          sx={{ textTransform: "none" }}
        >
          Venn diagram (2 circles)
        </Button>
        <Button
          size="small"
          variant="outlined"
          onClick={applyColourShapeTiles}
          sx={{ textTransform: "none" }}
        >
          Colour × shape tiles
        </Button>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
        <strong>Venn diagram (2 circles)</strong> builds the four regions as a grid: top-left cell =
        the overlap (in BOTH circles), the two single-circle cells, and bottom-right = outside both
        circles. Rename the two header labels (A / B) to your circle titles, then enter each region's
        numbers below. <strong>Colour × shape tiles</strong> builds the 3×4 grid and fills the seven
        blank answers (B ○ / B ☆ / …) with word alternatives — edit or add alternatives below.
      </Typography>

      <SpecialCharPanel onInsert={insertIntoActive} />
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
        Click a cell, then use Insert character (□ ○ ☆ △) for tile glyphs. Cells use plain text fields for simple answers.
      </Typography>

      <Paper variant="outlined" sx={{ overflow: "auto", mb: 3 }}>
        <Table size="small" sx={{ minWidth: 560, "& td, & th": { border: "1px solid #e0e0e0", p: 0.75 } }}>
          <TableBody>
            {grid.map((row, ri) => (
              <TableRow key={ri}>
                {row.map((cell, ci) => {
                  const editable = isEditableCell(cell);
                  return (
                    <TableCell key={ci} sx={{ verticalAlign: "top", minWidth: 110 }}>
                      <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={editable ? "editable" : "fixed"}
                        onChange={(_, val) => val && toggleCellType(ri, ci, val)}
                        sx={{ mb: 0.75, "& .MuiToggleButton-root": { py: 0.25, px: 0.75, fontSize: "0.65rem", textTransform: "none" } }}
                      >
                        <ToggleButton value="fixed">Fixed</ToggleButton>
                        <ToggleButton value="editable">Blank</ToggleButton>
                      </ToggleButtonGroup>
                      {editable ? (
                        <>
                          <Typography variant="caption" color="primary" sx={{ display: "block", fontWeight: 700, mb: 0.25 }}>
                            Blank #{cell.b + 1}
                          </Typography>
                          <TableCellEditor
                            value={blanks?.[cell.b]?.[0] ?? ""}
                            onChange={(val) => updateBlankAnswer(cell.b, 0, val)}
                            placeholder="e.g. B ○"
                            charTargetRef={charTargetRef}
                          />
                          {setBlankLabels ? (
                            <Box sx={{ mt: 0.5 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.25 }}>
                                Pre-filled (shown to student)
                              </Typography>
                              <TableCellEditor
                                value={blankLabels?.[cell.b] ?? ""}
                                onChange={(val) => updateBlankLabel(cell.b, val)}
                                placeholder="e.g. 12"
                                charTargetRef={charTargetRef}
                              />
                            </Box>
                          ) : null}
                        </>
                      ) : (
                        <TableCellEditor
                          value={cell.f ?? ""}
                          onChange={(val) => updateFixedText(ri, ci, val)}
                          placeholder="Header / label"
                          charTargetRef={charTargetRef}
                        />
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
        Correct answers per editable cell
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
        A cell can hold several numbers — type them comma-separated (e.g. <strong>3, 15, 21</strong>); the
        order the student types them does not matter. Use <strong>Pre-filled value</strong> for numbers
        already printed in that cell — those are shown to the student and not entered again. For a
        <strong> Venn diagram</strong>, each cell is one region (the overlap, each circle on its own, and
        outside both) — put that region's numbers in the cell, including any that belong to no circle.
      </Typography>
      {selectedEditable.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Mark at least one cell as Blank to define answers.
        </Typography>
      ) : (
        <>
        {selectedEditable.map(({ ri, ci, b }) => (
          <Box key={`${ri}-${ci}-${b}`} sx={{ mb: 2, p: 2, border: "1px solid #e0e0e0", borderRadius: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Chip
                label={`Cell row ${ri + 1}, col ${ci + 1} (blank ${b + 1})`}
                size="small"
                sx={{ background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)", color: "#fff", fontWeight: 500 }}
              />
              <TextField
                type="number"
                size="small"
                label="Marks"
                value={blankMarks?.[b] ?? 1}
                onChange={(e) => updateBlankMark(b, e.target.value)}
                inputProps={{ min: 1 }}
                sx={{ width: 88, ml: "auto" }}
              />
            </Box>
            {setBlankLabels ? (
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.4, fontWeight: 500 }}>
                  Pre-filled value (shown to student, not graded)
                </Typography>
                <TableCellEditor
                  value={blankLabels?.[b] ?? ""}
                  onChange={(val) => updateBlankLabel(b, val)}
                  placeholder="e.g. 12  (leave empty if nothing is pre-printed)"
                  charTargetRef={charTargetRef}
                />
              </Box>
            ) : null}
            {(blanks?.[b] || [""]).map((ans, ansIdx) => (
              <Box key={ansIdx} sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, mb: 1 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.4, fontWeight: 500 }}>
                    {ansIdx === 0 ? "Primary answer — separate multiple values with commas (e.g. 3, 15, 21)" : `Accepted answer ${ansIdx + 1}`}
                  </Typography>
                  <TableCellEditor
                    value={ans}
                    onChange={(val) => updateBlankAnswer(b, ansIdx, val)}
                    placeholder={ansIdx === 0 ? "e.g. B ○ or 3, 15, 21" : `Alternative ${ansIdx + 1}`}
                    charTargetRef={charTargetRef}
                  />
                </Box>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => removeAcceptedAnswer(b, ansIdx)}
                  disabled={(blanks?.[b] || []).length <= 1}
                  sx={{ mt: 3.5 }}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Box>
            ))}
            <Button size="small" startIcon={<Add />} onClick={() => addAcceptedAnswer(b)} sx={{ mt: 0.5 }}>
              Add Alternative
            </Button>
          </Box>
        ))}
        </>
      )}
    </Box>
  );
};

export { DEFAULT_GRID, reindexGrid, syncBlanksToGrid, countEditableCells };
export default TableFillEditor;
